/**
 * src/components/index.js  —  CENTRAL COMPONENT BARREL
 *
 * This file re-exports commonly used items so feature components can import
 * from a single location instead of many separate paths.
 *
 * ─── ORGANIZATION ────────────────────────────────────────────────────────────
 * Sections are marked with comments. Add new items to the correct section.
 * Prefer adding new imports directly to the consuming file over adding to this
 * barrel, to improve tree-shaking and make dependencies explicit.
 */

// ─── Routing ──────────────────────────────────────────────────────────────────
import { Link, NavLink } from 'react-router-dom';
import { withRouter } from '../util/withRouter';
import {
  getPaginationKey,
  getSavedPage,
  setSavedPage,
  clearSavedPage,
  clampPage,
} from '../util/paginationStorage';
import { Navigate as Redirect, Route, Routes, Routes as Switch, Navigate } from 'react-router-dom';
import asyncComponent from '../util/asyncComponent';

// ─── State Management ─────────────────────────────────────────────────────────
import { connect } from 'react-redux';

// ─── MUI Components ───────────────────────────────────────────────────────────
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Avatar from '@mui/material/Avatar';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import CardMedia from '@mui/material/CardMedia';
import Rating from '@mui/material/Rating';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Input from '@mui/material/Input';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

// ─── Notifications ────────────────────────────────────────────────────────────
import { toast, ToastContainer } from 'react-toastify';

// ─── HTTP Client ──────────────────────────────────────────────────────────────
import Axios from '../actions/Axios';

// ─── API Constants ────────────────────────────────────────────────────────────
// NOTE: Prefer importing directly from '../api/<domain>' or '../ApiUrl' for named
// exports. The default import here is kept for legacy component compatibility.
import ApiUrl from '../ApiUrl';

// ─── Internationalization ─────────────────────────────────────────────────────
import IntlMessages from '../util/IntlMessages';

// ─── Reactstrap Layout Primitives ────────────────────────────────────────────
import {
  CardBody,
  CardText,
  Container,
  Row,
  Col,
  CardHeader,
  CardImg,
  Card,
  CardTitle,
  CardSubtitle,
  Media,
  Badge,
  Alert,
} from 'reactstrap';

// ─── CSV Utilities ────────────────────────────────────────────────────────────
import CSVReader from 'react-csv-reader';

// ─── Rich Text Editor ─────────────────────────────────────────────────────────
// TODO: react-ckeditor-component is unmaintained. Plan to replace with a
// maintained alternative (e.g. @ckeditor/ckeditor5-react or TipTap).
import CKEditor from 'react-ckeditor-component';

// ─── Custom Form Fields ───────────────────────────────────────────────────────
import {
  TEXTFIELD,
  SUBMIT,
  RESET,
  PASSWORDFIELD,
  UPDATEPASSWORD,
  SELECT,
  SELECT2,
  BUTTON,
  FILEUPLOAD,
  DATEPICKER,
  CHECKBOX,
  GoogleAutocomplete,
} from './Common/InputFields';

// ─── Country Data ─────────────────────────────────────────────────────────────
import { COUNTRIES, PHONECODES } from './Common/countries';

// ─── Data Table Utilities ─────────────────────────────────────────────────────
import {
  DataTable,
  DataTableHead,
  DataTableBody,
  DataTablePagination,
  Action,
  ActionPopup,
  TableBody,
  TableRow,
  TableCell,
  moment,
} from './Common/DataTable';

// ─── UI Components ────────────────────────────────────────────────────────────
import InfoCard from '../components/InfoCard';
import ContainerHeader from '../components/ContainerHeader/index';
import Widget from '../components/Widget/index';
import CardBox from './CardBox/index';
import { DialogBox, DialogBox1, NavLinkButton, Accodian } from '../components/Common';
import NotFound from '../components/Common/extra/notFound';
import Loading from '../components/Common/extra/loading';
import ImagePreview from './Common/extra/ImagePreview';
import Status from './Status';

// ─── Validation Utilities ─────────────────────────────────────────────────────
import { checkValidations, checkError } from '../actions/helper';

// ─── Visual Constants ─────────────────────────────────────────────────────────
// These are also exported from src/utils/constants.js (preferred for new code).
const COLORS = '#237fff';
const Logo = 'assets/images/logo.png';
const noImage = ['', null, undefined, 'none'];

// ─── EXPORTS ──────────────────────────────────────────────────────────────────

export {
  // Routing
  Link,
  NavLink,
  withRouter,
  Redirect,
  Route,
  Switch,
  Routes,
  Navigate,
  asyncComponent,
  // State
  connect,
  // MUI
  Input,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Avatar,
  CircularProgress,
  TextField,
  Button,
  Grid,
  CardMedia,
  Rating,
  Paper,
  Chip,
  // Notifications
  toast,
  ToastContainer,
  // HTTP
  Axios,
  // API
  ApiUrl,
  // i18n
  IntlMessages,
  // Reactstrap
  CardBody,
  CardText,
  Container,
  Row,
  Col,
  CardHeader,
  CardImg,
  Card,
  CardTitle,
  CardSubtitle,
  Media,
  Badge,
  Alert,
  // CSV
  CSVReader,
  // Rich text (legacy)
  CKEditor,
  // Form fields
  TEXTFIELD,
  SUBMIT,
  RESET,
  PASSWORDFIELD,
  UPDATEPASSWORD,
  SELECT,
  SELECT2,
  BUTTON,
  FILEUPLOAD,
  DATEPICKER,
  CHECKBOX,
  GoogleAutocomplete,
  // Country data
  COUNTRIES,
  PHONECODES,
  // Data table
  DataTable,
  DataTableHead,
  DataTableBody,
  DataTablePagination,
  getPaginationKey,
  getSavedPage,
  setSavedPage,
  clearSavedPage,
  clampPage,
  Action,
  ActionPopup,
  TableBody,
  TableRow,
  TableCell,
  moment,
  // UI Components
  InfoCard,
  ContainerHeader,
  Widget,
  CardBox,
  DialogBox,
  DialogBox1,
  NavLinkButton,
  Accodian,
  NotFound,
  Loading,
  ImagePreview,
  Status,
  // Validation
  checkValidations,
  checkError,
  // Visual constants
  COLORS,
  Logo,
  noImage,
};
